import * as React from "react";
import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ExternalLink, History, XCircle } from "lucide-react";

import { Badge } from "@/lib/shadcn/ui/badge";
import { Button } from "@/lib/shadcn/ui/button";
import { Skeleton } from "@/lib/shadcn/ui/skeleton";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/lib/shadcn/ui/alert-dialog";
import { toast } from "@/lib/shadcn/hooks/use-toast";
import { ReservationHistoryDialog } from "@/lib/shadcn/components/reservation-history-dialog";
import { NeedsRefileItem } from "@/models/responses";
import { useGetNeedsRefileQuery, useMarkRefileReviewedMutation } from "@/store/hosthub-api";

const REFILE_NOTICE =
    "These reservations changed after they were filed with AADE (MyData) and/or the Short-Term Registry. " +
    "Nothing was automatically re-submitted. Review each and re-file manually if needed.";

function formatPeriod(startDate: Date | string, endDate: Date | string): string {
    const fmt = (v: Date | string) => {
        const d = v instanceof Date ? v : new Date(v);
        return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-GB");
    };
    return `${fmt(startDate)} → ${fmt(endDate)}`;
}

const RefileSkeleton: React.FC = () => (
    <div data-testid="refile-skeleton" className="space-y-3" aria-hidden="true">
        {[0, 1, 2].map((i) => (
            <div key={i} className="space-y-2 rounded-md border p-4">
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-3 w-72" />
                <Skeleton className="h-3 w-40" />
            </div>
        ))}
    </div>
);

interface RefileRowProps {
    item: NeedsRefileItem;
    onViewHistory: (item: NeedsRefileItem) => void;
    onMarkReviewed: (item: NeedsRefileItem) => void;
}

/**
 * A single flagged reservation. Rendered as a stacked card; on >=sm screens the
 * inner layout spreads horizontally. There is no fixed-column table here — the
 * card layout collapses cleanly to a single column on mobile (ui-spec Screen 3).
 */
const RefileRow: React.FC<RefileRowProps> = ({ item, onViewHistory, onMarkReviewed }) => (
    <li
        data-testid={`refile-row-${item.reservationId}`}
        className="flex flex-col gap-3 rounded-md border p-4 sm:flex-row sm:items-start sm:justify-between"
    >
        <div className="flex flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{item.reservationCode}</span>
                <span className="text-sm text-muted-foreground">{item.apartmentName}</span>
                <span className="text-sm text-muted-foreground">{formatPeriod(item.startDate, item.endDate)}</span>
            </div>
            <p className="text-sm text-slate-700">
                <span className="font-medium">What changed:</span> {item.reason}
            </p>
            <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-muted-foreground">Affected filings:</span>
                {item.isUploadedToMyData && (
                    <Badge data-testid="refile-chip-mydata" variant="secondary">
                        MyData
                    </Badge>
                )}
                {item.uploadedToShortTermRegistry && (
                    <Badge data-testid="refile-chip-str" variant="secondary">
                        STR
                    </Badge>
                )}
            </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
            <Button
                data-testid={`refile-view-history-${item.reservationId}`}
                variant="outline"
                size="sm"
                onClick={() => onViewHistory(item)}
                aria-label={`View version history for reservation ${item.reservationCode}`}
            >
                <History className="mr-1 h-4 w-4" aria-hidden="true" />
                View history
            </Button>
            <Button
                data-testid={`refile-mark-reviewed-${item.reservationId}`}
                variant="secondary"
                size="sm"
                onClick={() => onMarkReviewed(item)}
            >
                Mark reviewed
            </Button>
            <Button data-testid={`refile-mydata-link-${item.reservationId}`} variant="ghost" size="sm" asChild>
                <Link to="/admin/reservations">
                    Go to MyData upload
                    <ExternalLink className="ml-1 h-4 w-4" aria-hidden="true" />
                </Link>
            </Button>
        </div>
    </li>
);

/**
 * RefileReviewPage (ui-spec Screen 3) — the queue of reservations that changed
 * after being filed with AADE (MyData) and/or the Short-Term Registry. It never
 * re-submits anything; the operator reviews each row and either marks it
 * reviewed (clears the flag) or navigates to the existing MyData upload flow to
 * re-file manually. Empty is the healthy steady state.
 */
const RefileReviewPage: React.FC = () => {
    const { data: items = [], isLoading, isError, refetch } = useGetNeedsRefileQuery();
    const [markReviewed, { isLoading: isMarking }] = useMarkRefileReviewedMutation();

    const [historyFor, setHistoryFor] = useState<NeedsRefileItem | null>(null);
    const [confirmFor, setConfirmFor] = useState<NeedsRefileItem | null>(null);
    const [confirmation, setConfirmation] = useState<string | null>(null);

    const handleViewHistory = useCallback((item: NeedsRefileItem) => setHistoryFor(item), []);
    const handleMarkReviewedRequest = useCallback((item: NeedsRefileItem) => setConfirmFor(item), []);

    const handleConfirm = useCallback(async () => {
        if (!confirmFor) return;
        try {
            await markReviewed(confirmFor.reservationId).unwrap();
            setConfirmation("Marked reviewed");
            toast({ title: "Marked reviewed", description: `${confirmFor.reservationCode} cleared from the queue.` });
        } catch (error) {
            toast({
                title: "Error",
                description: `Failed to mark reviewed: ${JSON.stringify(error)}`,
                variant: "destructive",
            });
        } finally {
            setConfirmFor(null);
        }
    }, [confirmFor, markReviewed]);

    return (
        <div data-testid="refile-review-page" className="flex flex-col gap-4">
            <h1 className="text-3xl font-bold">Re-file review</h1>
            <div
                data-testid="refile-notice"
                role="status"
                className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800"
            >
                {REFILE_NOTICE}
            </div>

            {confirmation && (
                <div
                    data-testid="refile-toast"
                    role="status"
                    className="rounded-md border border-green-300 bg-green-50 px-3 py-2 text-sm text-green-800"
                >
                    {confirmation}
                </div>
            )}

            {isLoading && <RefileSkeleton />}

            {!isLoading && isError && (
                <div
                    data-testid="refile-error-panel"
                    role="alert"
                    className="flex flex-col items-center gap-4 py-8 text-center"
                >
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 text-red-600">
                        <XCircle className="h-6 w-6" aria-hidden="true" />
                    </div>
                    <p className="max-w-md text-sm text-slate-700">The re-file review list could not be loaded.</p>
                    <Button data-testid="refile-retry" onClick={() => void refetch()}>
                        Retry
                    </Button>
                </div>
            )}

            {!isLoading && !isError && items.length === 0 && (
                <div
                    data-testid="refile-empty"
                    className="flex flex-col items-center gap-3 py-12 text-center"
                >
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-green-600">
                        <CheckCircle2 className="h-6 w-6" aria-hidden="true" />
                    </div>
                    <p className="text-sm text-slate-700">No reservations need re-file review.</p>
                </div>
            )}

            {!isLoading && !isError && items.length > 0 && (
                <ul data-testid="refile-list" className="space-y-3">
                    {items.map((item) => (
                        <RefileRow
                            key={item.reservationId}
                            item={item}
                            onViewHistory={handleViewHistory}
                            onMarkReviewed={handleMarkReviewedRequest}
                        />
                    ))}
                </ul>
            )}

            <ReservationHistoryDialog
                open={historyFor !== null}
                reservationId={historyFor?.reservationId ?? null}
                reservationCode={historyFor?.reservationCode ?? ""}
                onClose={() => setHistoryFor(null)}
            />

            <AlertDialog open={confirmFor !== null} onOpenChange={(open) => !open && setConfirmFor(null)}>
                <AlertDialogContent data-testid="refile-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            Mark {confirmFor?.reservationCode ?? ""} as reviewed?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            This clears the re-file flag. Nothing is re-submitted — re-file manually first if needed.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="refile-confirm-cancel" disabled={isMarking}>
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction data-testid="refile-confirm-yes" onClick={handleConfirm} disabled={isMarking}>
                            Mark reviewed
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default RefileReviewPage;
